import { useEffect, useState } from "react";
import { cx } from "@its-engineering-and-development/ui-foundation/utils";
import { Spinner } from "@app/components/base/spinner/spinner";
import type { ModulePermissionCell, PermissionAccess, PermissionModule, PermissionRole } from "@app/types/permissions";
import { PermissionLegend, PermissionPill } from "./permission-pill";

function collectExpandableModuleIds(modules: PermissionModule[]): string[] {
    return modules.filter((module) => Boolean(module.children?.length)).map((module) => module.id);
}

function IconChevron({ expanded, className }: { expanded: boolean; className?: string }) {
    return (
        <svg
            className={cx("h-4 w-4 shrink-0 text-gray-500 transition-transform", expanded && "rotate-180", className)}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
    );
}

function ModuleRow({
    module,
    depth,
    roles,
    expandedModules,
    pendingCells,
    onToggleModule,
    getPermission,
    onPermissionChange,
    canEdit,
}: {
    module: PermissionModule;
    depth: number;
    roles: PermissionRole[];
    expandedModules: Set<string>;
    pendingCells: ReadonlySet<string>;
    onToggleModule: (moduleId: string) => void;
    getPermission: (moduleId: string, roleId: string) => ModulePermissionCell | null;
    onPermissionChange: (moduleId: string, roleId: string, access: PermissionAccess) => void;
    canEdit: boolean;
}) {
    const hasChildren = Boolean(module.children?.length);
    const isExpanded = expandedModules.has(module.id);

    return (
        <>
            <tr className={cx("border-t border-gray-100", hasChildren ? "bg-gray-50 hover:bg-gray-100/50" : "hover:bg-gray-50/60")}>
                <th
                    scope="row"
                    className={cx("sticky left-0 z-10 px-4 py-3 text-left text-sm font-medium text-gray-900", hasChildren ? "bg-gray-50" : "bg-white")}
                >
                    <div className="flex items-center gap-2" style={{ paddingLeft: `${depth * 1.25}rem` }}>
                        {hasChildren ? (
                            <button
                                type="button"
                                className="inline-flex items-center gap-2 rounded-md text-left hover:text-brand-700"
                                aria-expanded={isExpanded}
                                onClick={() => onToggleModule(module.id)}
                            >
                                <IconChevron expanded={isExpanded} />
                                <span className="font-semibold text-gray-700">{module.label}</span>
                            </button>
                        ) : (
                            <span className={cx(depth > 0 ? "text-gray-500" : "text-gray-900")}>{depth > 0 ? `— ${module.label}` : module.label}</span>
                        )}
                    </div>
                </th>
                {roles.map((role) => {
                    const cell = hasChildren ? null : getPermission(module.id, role.id);
                    const isPending = !hasChildren && pendingCells.has(`${module.id}:${role.id}`);
                    return (
                        <td key={role.id} className="px-4 py-3 text-center">
                            <div className="flex justify-center">
                                {isPending ? (
                                    <Spinner className="h-4 w-4 text-brand-500" />
                                ) : (
                                    <PermissionPill
                                        access={cell?.access ?? null}
                                        editable={cell?.editable ?? false}
                                        canEdit={canEdit}
                                        onChange={(access) => onPermissionChange(module.id, role.id, access)}
                                    />
                                )}
                            </div>
                        </td>
                    );
                })}
            </tr>
            {hasChildren && isExpanded
                ? module.children?.map((child) => (
                      <ModuleRow
                          key={child.id}
                          module={child}
                          depth={depth + 1}
                          roles={roles}
                          expandedModules={expandedModules}
                          pendingCells={pendingCells}
                          onToggleModule={onToggleModule}
                          getPermission={getPermission}
                          onPermissionChange={onPermissionChange}
                          canEdit={canEdit}
                      />
                  ))
                : null}
        </>
    );
}

export function ModuleAccessMatrix({
    roles,
    modules,
    pendingCells,
    getPermission,
    onPermissionChange,
    canEdit,
}: {
    roles: PermissionRole[];
    modules: PermissionModule[];
    pendingCells: ReadonlySet<string>;
    getPermission: (moduleId: string, roleId: string) => ModulePermissionCell | null;
    onPermissionChange: (moduleId: string, roleId: string, access: PermissionAccess) => void;
    canEdit: boolean;
}) {
    const [isSectionOpen, setIsSectionOpen] = useState(true);
    const [expandedModules, setExpandedModules] = useState<Set<string>>(() => new Set(collectExpandableModuleIds(modules)));

    // Expand any group modules that arrive after the initial (empty) render
    useEffect(() => {
        const expandable = collectExpandableModuleIds(modules);
        if (expandable.length > 0) {
            setExpandedModules((prev) => {
                const next = new Set(prev);
                expandable.forEach((id) => next.add(id));
                return next;
            });
        }
    }, [modules]);

    const onToggleModule = (moduleId: string) => {
        setExpandedModules((prev) => {
            const next = new Set(prev);
            if (next.has(moduleId)) next.delete(moduleId);
            else next.add(moduleId);
            return next;
        });
    };

    return (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xs">
            <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-4 text-left hover:bg-gray-50"
                aria-expanded={isSectionOpen}
                onClick={() => setIsSectionOpen((prev) => !prev)}
            >
                <IconChevron expanded={isSectionOpen} />
                <span className="text-base font-semibold text-gray-900">Module access by role</span>
            </button>

            {isSectionOpen ? (
                <>
                    <div className="overflow-x-auto">
                        <table className="min-w-[56rem] w-full border-collapse">
                            <thead>
                                <tr className="border-y border-gray-200 bg-gray-50">
                                    <th
                                        scope="col"
                                        className="sticky left-0 z-20 bg-gray-50 px-4 py-3 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase"
                                    >
                                        Module
                                    </th>
                                    {roles.map((role) => (
                                        <th key={role.id} scope="col" className="min-w-[9.5rem] px-4 py-3 text-center">
                                            <div className="text-sm font-semibold text-gray-900">{role.label}</div>
                                            {role.subtitle ? (
                                                <p className="mt-1 text-[11px] leading-snug font-normal text-utility-brand-700">{role.subtitle}</p>
                                            ) : null}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {modules.map((module) => (
                                    <ModuleRow
                                        key={module.id}
                                        module={module}
                                        depth={0}
                                        roles={roles}
                                        expandedModules={expandedModules}
                                        pendingCells={pendingCells}
                                        onToggleModule={onToggleModule}
                                        getPermission={getPermission}
                                        onPermissionChange={onPermissionChange}
                                        canEdit={canEdit}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <PermissionLegend />
                </>
            ) : null}
        </section>
    );
}
